import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {Star, UserCheck, ClipboardList, Users, ChevronRight, Trash2, Calendar, Clock, SlidersHorizontal} from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import EvaluationFormModal from '@/components/EvaluationFormModal'
import { useAuth } from '@/hooks/useAuth'
import { useLinks, type WorkLink } from '@/hooks/useLinks'
import { useEvaluations } from '@/hooks/useEvaluations'
import { EVALUATION_ASPECTS, type EvaluationScores } from '@/lib/evaluationAspects'
import { useCompanyAspects, type CompanyAspect } from '@/hooks/useCompanyAspects'
import { lumi } from '@/lib/lumi'

function avgBadgeColor(avg: number) {
  if (avg >= 8) return 'bg-emerald-50 text-emerald-700'
  if (avg >= 6) return 'bg-amber-50 text-amber-700'
  return 'bg-red-50 text-red-600'
}

/** Ventana de 24 h para evaluar tras finalizar el vínculo */
function withinGraceWindow(link: WorkLink): boolean {
  if (link.status !== 'finalizada') return true
  const ref = link.endDate || link.updatedAt
  if (!ref) return false
  return Date.now() - new Date(ref).getTime() <= 24 * 60 * 60 * 1000
}

function currentPeriod(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function EmpresaEvaluaciones() {
  const { user } = useAuth()
  const { links, loading: loadingLinks, fetchByCompany } = useLinks()
  const { evaluations, loading: loadingEvals, fetchByCompany: fetchEvals, createEvaluation, deleteEvaluation } =
    useEvaluations()
  const { fetchEffectiveAspects } = useCompanyAspects()
  const [activeLink, setActiveLink] = useState<WorkLink | null>(null)

  // Aspectos efectivos configurados por la empresa
  const [companyAspects, setCompanyAspects] = useState<CompanyAspect[]>([])

  // Mapa userId -> fullName / photo cargado desde collaborators
  const [nameMap, setNameMap] = useState<Record<string, string>>({})
  const [photoMap, setPhotoMap] = useState<Record<string, string>>({})

  const loadAll = useCallback(() => {
    if (user) {
      fetchByCompany(user.userId)
      fetchEvals(user.userId)
    }
  }, [user])

  useEffect(() => {
    loadAll()
  }, [user])

  // Cargar aspectos efectivos de la empresa al montar
  useEffect(() => {
    if (!user?.userId) return
    fetchEffectiveAspects(user.userId).then((aspects) => {
      setCompanyAspects(aspects)
    })
  }, [user?.userId])

  // Colaboradores activos + finalizados dentro de ventana de 24 h
  const period = currentPeriod()
  const activeCollaborators = links.filter((l) => l.status === 'aceptada')
  const graceCollaborators = links.filter(
    (l) => l.status === 'finalizada' && withinGraceWindow(l)
  )
  // Unificamos: activos primero, luego en gracia (sin duplicar)
  const evaluableLinks = [
    ...activeCollaborators,
    ...graceCollaborators.filter(
      (g) => !activeCollaborators.some((a) => a.collaboratorUserId === g.collaboratorUserId)
    ),
  ]

  // Cargar nombres y fotos SIEMPRE desde collaborators (fuente de verdad) — ignora collaboratorName de BD
  useEffect(() => {
    const allLinks = links.filter((l) => l.collaboratorUserId)
    if (allLinks.length === 0) return
    // Siempre resolvemos TODOS los userId únicos para garantizar fullName real
    const ids = [...new Set(allLinks.map((l) => l.collaboratorUserId))]
    Promise.all(
      ids.map((uid) =>
        lumi.entities.collaborators
          .list({ filter: { userId: uid }, limit: 1 })
          .then((r) => {
            const c = r.list[0] as { fullName?: string; email?: string; photoUrl?: string } | undefined
            if (c?.fullName) return { uid, name: c.fullName, photo: c.photoUrl || '' }
            if (c?.email) return { uid, name: c.email, photo: c.photoUrl || '' }
            return null
          })
          .catch(() => null)
      )
    ).then((results) => {
      const nm: Record<string, string> = {}
      const pm: Record<string, string> = {}
      results.forEach((r) => { if (r) { nm[r.uid] = r.name; pm[r.uid] = r.photo } })
      if (Object.keys(nm).length > 0) {
        setNameMap((prev) => ({ ...prev, ...nm }))
        setPhotoMap((prev) => ({ ...prev, ...pm }))
      }
    })
  }, [links])

  // Helper para obtener nombre real — SIEMPRE prioriza fullName de la BD de collaborators
  const getName = (link: WorkLink) =>
    nameMap[link.collaboratorUserId] || link.collaboratorEmail || 'Colaborador'
  const getPhoto = (link: WorkLink) => photoMap[link.collaboratorUserId] || ''

  // Verifica si ya existe evaluación para este colaborador en el periodo actual
  const alreadyEvaluatedThisMonth = (link: WorkLink) =>
    evaluations.some(
      (e) => e.collaboratorUserId === link.collaboratorUserId && e.period === period
    )

  const handleSubmit = async (payload: {
    period: string
    scores: EvaluationScores
    average: number
    comments: string
    observations: string
    recommendations: string
  }) => {
    if (!user || !activeLink) return
    try {
      await createEvaluation({
        linkId: activeLink._id,
        companyUserId: user.userId,
        companyName: activeLink.companyName,
        collaboratorUserId: activeLink.collaboratorUserId,
        collaboratorName: getName(activeLink),
        area: activeLink.area,
        ...payload,
        creator: user.userId,
      })
      setActiveLink(null)
      fetchEvals(user.userId)
    } catch (e) {
      console.error('Error al guardar evaluacion:', e)
      alert('Ocurrió un error al guardar la evaluación. Intenta de nuevo.')
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar esta evaluación? Esta acción no se puede deshacer.')) return
    try {
      await deleteEvaluation(id)
      if (user) fetchEvals(user.userId)
    } catch (e) {
      console.error('Error al eliminar evaluacion:', e)
      alert('No se pudo eliminar la evaluación.')
    }
  }

  const loading = loadingLinks || loadingEvals

  return (
    <EmpresaLayout
      title="Evaluaciones"
      subtitle="Evalúa mensualmente el desempeño de tus colaboradores (escala 1 a 10). Solo una evaluación por colaborador por mes."
    >
      {/* Colaboradores evaluables */}
      <section className="mb-8">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900">
          <UserCheck size={18} className="text-amber-500" /> Colaboradores a evaluar
        </h2>

        {loading && evaluableLinks.length === 0 ? (
          <p className="text-sm text-slate-400">Cargando...</p>
        ) : evaluableLinks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <Users size={28} className="mx-auto text-slate-300" />
            <p className="mt-3 text-sm font-medium text-slate-700">Aún no tienes colaboradores vinculados</p>
            <p className="mt-1 text-sm text-slate-500">
              Las evaluaciones se habilitan tras aceptar una vinculación laboral.
            </p>
            <Link
              to="/empresa/colaboradores"
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Gestionar vinculaciones <ChevronRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {evaluableLinks.map((link) => {
              const collabEvals = evaluations.filter((e) => e.collaboratorUserId === link.collaboratorUserId)
              const lastAvg = collabEvals.length > 0 ? collabEvals[0].average : null
              const evaluated = alreadyEvaluatedThisMonth(link)
              const isGrace = link.status === 'finalizada'
              return (
                <div key={link._id} className={`rounded-xl border bg-white p-4 ${isGrace ? 'border-amber-200' : 'border-slate-200'}`}>
                  {isGrace && (
                    <div className="mb-3 flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700">
                      <Clock size={12} /> Vinculación finalizada · ventana de 24 h
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    {getPhoto(link) ? (
                      <img src={getPhoto(link)} alt={getName(link)} className="h-10 w-10 rounded-full object-cover border-2 border-white shadow-sm shrink-0" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-400 text-sm font-bold text-slate-900 shrink-0">
                        {getName(link).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900">{getName(link)}</p>
                      <p className="truncate text-xs text-slate-500">{link.position || 'Colaborador'}</p>
                    </div>
                    {lastAvg !== null && (
                      <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${avgBadgeColor(lastAvg)}`}>
                        {lastAvg.toFixed(1)}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                    <span>{collabEvals.length} evaluación(es)</span>
                  </div>
                  {evaluated ? (
                    <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
                      <Star size={12} className="fill-emerald-500 text-emerald-500" />
                      Ya evaluaste a este colaborador este mes
                    </div>
                  ) : (
                    <button
                      onClick={() => setActiveLink(link)}
                      className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-amber-400 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
                    >
                      <Star size={15} /> Evaluar
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Historial de evaluaciones */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900">
          <ClipboardList size={18} className="text-amber-500" /> Historial de evaluaciones
        </h2>

        {evaluations.length === 0 ? (
          <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
            Aún no has registrado evaluaciones.
          </p>
        ) : (
          <div className="space-y-3">
            {evaluations.map((evalItem) => (
              <div key={evalItem._id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{nameMap[evalItem.collaboratorUserId] || 'Colaborador'}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                      <Calendar size={12} /> Periodo {evalItem.period}
                      {evalItem.area ? ` · ${evalItem.area}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-md px-2.5 py-1 text-sm font-bold ${avgBadgeColor(evalItem.average)}`}>
                      {evalItem.average.toFixed(1)}
                    </span>
                    <button
                      onClick={() => handleDelete(evalItem._id)}
                      className="rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* Mini desglose — usa aspectos configurados por la empresa, con fallback a base */}
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3 lg:grid-cols-5">
                  {(companyAspects.length > 0 ? companyAspects : EVALUATION_ASPECTS).map((a) => (
                    evalItem.scores?.[a.key] !== undefined ? (
                      <div key={a.key} className="flex items-center justify-between text-xs">
                        <span className="truncate text-slate-500">{a.label}</span>
                        <span className="font-semibold text-slate-800">{evalItem.scores[a.key]}</span>
                      </div>
                    ) : null
                  ))}
                </div>

                {(evalItem.comments || evalItem.recommendations) && (
                  <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-600">
                    {evalItem.comments && <p><span className="font-medium text-slate-700">Comentarios:</span> {evalItem.comments}</p>}
                    {evalItem.recommendations && <p><span className="font-medium text-slate-700">Recomendaciones:</span> {evalItem.recommendations}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Aviso de aspectos personalizados activos */}
      {companyAspects.length > 0 && companyAspects.some(a => a.custom) && (
        <div className="mt-6 flex items-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-2.5 text-xs text-indigo-700">
          <SlidersHorizontal size={14} className="shrink-0" />
          Estás usando una configuración personalizada de aspectos de evaluación.
          <Link to="/empresa/aspectos" className="ml-auto font-semibold underline underline-offset-2 hover:text-indigo-900">Editar</Link>
        </div>
      )}

      {activeLink && (
        <EvaluationFormModal
          link={activeLink}
          aspects={companyAspects.length > 0 ? companyAspects : undefined}
          existingEvaluations={evaluations.map((e) => ({
            period: e.period,
            collaboratorUserId: e.collaboratorUserId,
            linkId: e.linkId,
          }))}
          onClose={() => setActiveLink(null)}
          onSubmit={handleSubmit}
        />
      )}
    </EmpresaLayout>
  )
}

export default EmpresaEvaluaciones
